import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, throttle, isEqual } from 'lodash';

import useRegistrationEntitiesQuery from '../../autoIso/hooks/useAutoIsoEntitiesQuery';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { setIsActive, sortPlayers } from '@/features/teams/functions';
import { AutoIsoModel, AutoIsoObjectType } from '@/features/autoIso/types/AutoIsoModel';
import { TeamModel } from '../types/TeamModel';
import { AutoIsoTeamSelectorState, CategoryPlayers, ProcessedTeam } from '../types/AutoIso';
import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';



export const useAutoIsoTeamSelectorState = (): AutoIsoTeamSelectorState => {
  // Server State
  const { gameStateManager } = useServerStateContext();
  const autoIsoQuery = useRegistrationEntitiesQuery();
  const { homeTeam, awayTeam, list = [] } = autoIsoQuery?.data || {};

  // Local State
  const [gameTrackingEntities, setGameTrackingEntities] = useState<AutoIsoModel[]>([]);
  const [includeInactivePlayers, setIncludeInactivePlayers] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activeEntities, setActiveEntities] = useState<number[]>([]);

  // Throttled state setter for 60Hz updates - only update UI every 100ms (10fps)
  const throttledSetGameTrackingEntities = useCallback(
    throttle((entities: AutoIsoModel[]) => {
      setGameTrackingEntities(entities);
    }, 100, { leading: true, trailing: true }),
    []
  );

  // Event Handlers
  useEffect(() => {
    if (!gameStateManager) return;

    const handleTrackingEntitiesChange = (trackingEntities: AutoIsoModel[]) => {
      // Use throttled setter to reduce re-renders from 60Hz to ~10Hz
      throttledSetGameTrackingEntities(trackingEntities);
    };

    gameStateManager.on('trackingEntitiesChange', handleTrackingEntitiesChange);
    return () => {
      gameStateManager.off?.('trackingEntitiesChange', handleTrackingEntitiesChange);
      throttledSetGameTrackingEntities.cancel(); // Clean up throttle on unmount
    };
  }, [gameStateManager, throttledSetGameTrackingEntities]);

  useEffect(() => {
    if (gameTrackingEntities?.length) {
      const newIds = gameTrackingEntities.map((e) => e.id);
      if (!isEqual(newIds, activeEntities)) {
        setActiveEntities(newIds);
      }
    }
  }, [gameTrackingEntities, activeEntities]);

  // Memoized Filtering Logic
  const filteredRegistrationEntities = useMemo(() => {
    return list
      .filter((entity) => {
        if (!includeInactivePlayers && !activeEntities.includes(entity.id)) {
          return false;
        }

        if (!searchValue) return true;

        const searchLower = searchValue.toLowerCase();


        return (
          entity.displayName.toLowerCase().includes(searchLower) ||
          (entity.type === AutoIsoObjectType.Player && entity.playerNumber?.toString().includes(searchValue))
        );
      })
      .map(setIsActive(activeEntities));
  }, [list, activeEntities, includeInactivePlayers, searchValue]);

  // Add refs to store previous values for comparison
  const prevFilteredEntitiesRef = useRef<AutoIsoModel[]>([]);
  const prevHomeTeamRef = useRef<TeamModel | null>(undefined);
  const prevAwayTeamRef = useRef<TeamModel | null>(undefined);
  const prevProcessedTeamsRef = useRef<ProcessedTeam[]>([]);


  // Memoized Team Processing with optimization
  const processedTeams = useMemo((): ProcessedTeam[] => {
    // Check if inputs have changed
    const entitiesChanged = !isEqual(prevFilteredEntitiesRef.current, filteredRegistrationEntities);
    const homeTeamChanged = !isEqual(prevHomeTeamRef.current, homeTeam);
    const awayTeamChanged = !isEqual(prevAwayTeamRef.current, awayTeam);

    // If nothing has changed, return the previous result
    if (!entitiesChanged && !homeTeamChanged && !awayTeamChanged && prevProcessedTeamsRef.current.length > 0) {
      return prevProcessedTeamsRef.current;
    }

    // Update refs with current values for next comparison
    prevFilteredEntitiesRef.current = [...filteredRegistrationEntities];
    prevHomeTeamRef.current = homeTeam;
    prevAwayTeamRef.current = awayTeam;

    // Process teams only if something has changed
    const newProcessedTeams = [homeTeam, awayTeam]
      .map((team) => {
        if (!team) return null;

        const teamPlayers = filteredRegistrationEntities.filter(
          (entity) => entity.type === AutoIsoObjectType.Player && entity?.playerTeamId === team.id,
        );

        const categorizedPlayers = teamPlayers.reduce((acc, player) => {
          const category = player.playerCategory || 'Uncategorized';
          //@ts-expect-error
          player.team = {
            id: team.id,
            displayName: team.displayName,
            abbreviation: team.abbreviation,
            logoUrl: getTeamLogoUrl(team),
            colors: team.teamColors,
          }

          return {
            ...acc,
            [category]: [...(acc[category] || []), player],
          };
        }, {} as CategoryPlayers);

        // Sort players within each category
        Object.keys(categorizedPlayers).forEach((category) => {
          categorizedPlayers[category].sort(sortPlayers);
        });

        return {
          ...team,
          players: categorizedPlayers,
        };
      })
      .filter(Boolean) as ProcessedTeam[];

    // Store new result for future comparisons
    prevProcessedTeamsRef.current = newProcessedTeams;

    return newProcessedTeams;
  }, [filteredRegistrationEntities, homeTeam, awayTeam]);

  // Debounced Search Handler
  const debouncedSearch = useCallback(
    debounce((value: string) => setSearchValue(value), 300),
    [],
  );

  const handleSearchChange = (value: string) => {
    debouncedSearch(value);
  };

  // Reset Function
  const reset = useCallback(() => {
    setSearchValue('');
    setIncludeInactivePlayers(true); // Show all players including inactive ones
    debouncedSearch.cancel(); // Cancel any pending debounced search
  }, [debouncedSearch]);



  return {
    processedTeams,
    includeInactivePlayers,
    setIncludeInactivePlayers,
    handleSearchChange,
    reset,
  };
};

export default useAutoIsoTeamSelectorState;